/**
 * Wire types of the Workflows API (`Gasbi.Workflows.Api`, the
 * `WorkflowDefinitions` and `WorkflowDefinitionBoard` controllers), one to one
 * with its OpenAPI document. The builder never reads them directly:
 * `mappers.ts` turns them into the models of `types.ts` and back.
 *
 * Nullable fields are optional: System.Text.Json keeps a property's default
 * when it is missing, but rejects `null` in place of a list (`Document.ValueIsNull`).
 */

export type WorkflowTypeDto = 'Background' | 'Foreground' | 'Subworkflow';

export type WorkflowCreationModeDto = 'Manual' | 'Agent';

export type WorkflowDefinitionVersionStateDto =
  | 'Draft'
  | 'Published'
  | 'Retired';

export type WorkflowStatusCategoryDto =
  | 'ToDo'
  | 'InProgress'
  | 'Done'
  | 'Rejected';

export type ApproverRefKindDto =
  | 'Role'
  | 'Department'
  | 'Employee'
  | 'Expression';

export interface ApproverRefDto {
  kind: ApproverRefKindDto;
  value?: string | null;
}

export interface ApprovalPolicyDto {
  approvers?: ApproverRefDto[] | null;
  rule: 'All' | 'Any' | 'Quorum';
  requiredCount?: number | null;
  finalCheckByLast: boolean;
}

export interface RejectionPolicyDto {
  target: 'ReturnToStatus' | 'ExitToStatus';
  stateKey?: string | null;
  reasonRequired: boolean;
}

export interface TransitionConditionDto {
  kind:
    | 'OnlyApprovers'
    | 'RoleIn'
    | 'DepartmentIn'
    | 'CaseManagerOnly'
    | 'Expression';
  refs?: ApproverRefDto[] | null;
  expression?: string | null;
}

export interface FieldDefDto {
  key: string;
  name: string;
  type: 'Text' | 'Number' | 'Boolean' | 'Date';
  required: boolean;
}

export interface InputRequestDto {
  fields?: FieldDefDto[] | null;
  resolutionRequired: boolean;
}

export interface TransitionValidatorDto {
  kind: 'FieldRequired' | 'Expression' | 'DocumentSigned' | 'SurveyCompleted';
  fieldKey?: string | null;
  expression?: string | null;
  errorMessage?: string | null;
}

export interface SlotActionValueDto {
  key?: string | null;
  value?: string | null;
}

export interface SlotActionDto {
  slot: 'BeforeApproval' | 'AfterFinalCheck' | 'OnReject';
  order: number;
  origin: WorkflowCreationModeDto;
  blockType?: string | null;
  inputs?: SlotActionValueDto[] | null;
  fieldUpdates?: SlotActionValueDto[] | null;
  afterRun?: string | null;
  fragmentId?: string | null;
}

export interface TransitionTriggerDto {
  kind:
    | 'DomainEvent'
    | 'IntegrationCallback'
    | 'Timer'
    | 'SubworkflowCompleted';
  parameter?: string | null;
  autoExecute: boolean;
}

/** `amount` is hours, or business days with `businessDays`. */
export interface SlaPeriodDto {
  amount?: number | null;
  businessDays: boolean;
  settingKey?: string | null;
}

export interface TransitionSlaDto {
  warnAfter: SlaPeriodDto;
  breachAfter: SlaPeriodDto;
  escalation?: ApproverRefDto | null;
}

export interface TransitionPropertiesDto {
  buttonName?: string | null;
  order: number;
  color?: string | null;
  confirmBeforeExecute: boolean;
  visibleInStatusMenu: boolean;
}

export interface TransitionRulesContentDto {
  approval?: ApprovalPolicyDto | null;
  rejection?: RejectionPolicyDto | null;
  conditions?: TransitionConditionDto[] | null;
  inputRequest?: InputRequestDto | null;
  validators?: TransitionValidatorDto[] | null;
  actions?: SlotActionDto[] | null;
  triggers?: TransitionTriggerDto[] | null;
  sla?: TransitionSlaDto | null;
  properties?: TransitionPropertiesDto | null;
}

export interface StateDefinitionDto {
  key: string;
  name: string;
  category: WorkflowStatusCategoryDto;
  color?: string | null;
  isInitial: boolean;
  isFinal: boolean;
  resolutions?: string[] | null;
  stepForm?: InputRequestDto | null;
  sla?: TransitionSlaDto | null;
}

/** `from: ['*']` (`ANY_STATE`) — a global transition out of any state. */
export interface TransitionDefinitionDto {
  key: string;
  name: string;
  from: string[];
  to: string;
  rules?: TransitionRulesContentDto | null;
}

export interface AutomationTriggerDto {
  kind: 'TransitionExecuted' | 'StatusEntered';
  /** Transition or state keys; empty — any. */
  keys?: string[] | null;
}

export interface AutomationConditionDto {
  kind:
    | 'PreviousStatusIn'
    | 'PreviousStatusNotIn'
    | 'TransitionIs'
    | 'StepFormValueEquals';
  keys?: string[] | null;
  stateKey?: string | null;
  fieldKey?: string | null;
  value?: string | null;
}

export interface AutomationActionDto {
  kind: 'MoveToStatus' | 'SendEmail' | 'Notify' | 'UpdateField';
  targetStateKey?: string | null;
  parameters?: Record<string, string> | null;
}

export interface AutomationDefinitionDto {
  key: string;
  isEnabled: boolean;
  /** `yyyy-MM-dd`. */
  activeFrom?: string | null;
  activeTo?: string | null;
  trigger: AutomationTriggerDto;
  conditions?: AutomationConditionDto[] | null;
  actions?: AutomationActionDto[] | null;
}

export interface WorkflowDocumentDto {
  states: StateDefinitionDto[];
  transitions: TransitionDefinitionDto[];
  automations: AutomationDefinitionDto[];
}

/** `GET /workflow-definitions`. */
export interface WorkflowDefinitionDto {
  id: string;
  name?: string | null;
  description?: string | null;
  type: WorkflowTypeDto;
  publishedVersionId?: string | null;
  publishedVersionNumber?: number | null;
  draftVersionId?: string | null;
  createdAt: string;
  createdBy?: string | null;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  updatedByName?: string | null;
}

/** `GET /workflow-definitions/{id}`. */
export interface WorkflowDefinitionDetailsDto {
  id: string;
  name?: string | null;
  description?: string | null;
  type: WorkflowTypeDto;
  versionId?: string | null;
  publishedVersionNumber?: number | null;
  createdAt: string;
  createdBy?: string | null;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  updatedByName?: string | null;
  publishedAt?: string | null;
  publishedBy?: string | null;
  publishedByName?: string | null;
  creationMode: WorkflowCreationModeDto;
  document?: WorkflowDocumentDto | null;
  migrationMap?: Record<string, string> | null;
}

/** `GET /workflow-definitions/{id}/versions`. */
export interface WorkflowDefinitionVersionListItemDto {
  id: string;
  number: number;
  state: WorkflowDefinitionVersionStateDto;
  creationMode: WorkflowCreationModeDto;
  documentRevision: number;
  basedOnVersionId?: string | null;
  createdAt: string;
  createdBy?: string | null;
  createdByName?: string | null;
  updatedAt?: string | null;
  updatedBy?: string | null;
  updatedByName?: string | null;
  publishedAt?: string | null;
  publishedBy?: string | null;
  publishedByName?: string | null;
}

/** `GET /workflow-definitions/{id}/versions/{versionId}`. */
export interface WorkflowDefinitionVersionDto extends WorkflowDefinitionVersionListItemDto {
  definitionId: string;
  document: WorkflowDocumentDto;
  migrationMap?: Record<string, string> | null;
}

/** `GET /workflow-definitions/status-options`: no key, one row per distinct status. */
export interface WorkflowDefinitionStateOptionDto {
  name?: string | null;
  color?: string | null;
  category: WorkflowStatusCategoryDto;
}

export interface CreateWorkflowDefinitionRequestDto {
  name: string;
  description: string;
  type: WorkflowTypeDto;
  creationMode: WorkflowCreationModeDto;
}

export interface CreateWorkflowDefinitionResultDto {
  id: string;
  draftVersionId: string;
}

export interface UpdateWorkflowDefinitionDetailsRequestDto {
  id: string;
  name: string;
  description: string;
}

export interface CreateDraftRequestDto {
  creationMode: WorkflowCreationModeDto;
}

export interface CreateDraftFromPublishedResultDto {
  versionId: string;
  number: number;
}

export interface SaveDraftRequestDto {
  document: WorkflowDocumentDto;
  expectedRevision: number | null;
}

export interface SaveDraftDocumentResultDto {
  versionId: string;
  documentRevision: number;
}

export interface ValidateDocumentRequestDto {
  document: WorkflowDocumentDto;
}

export interface WorkflowDocumentErrorDto {
  /** E.g. `transitions[0].rules.approval.requiredCount`. */
  path?: string | null;
  code?: string | null;
  message?: string | null;
  hint?: string | null;
  /** `false` — the draft saves, only publishing is blocked. */
  blocksDraftSave: boolean;
}

export interface ValidateWorkflowDocumentResultDto {
  canSaveDraft: boolean;
  canPublish: boolean;
  errors?: WorkflowDocumentErrorDto[] | null;
}

export interface PublishRequestDto {
  expectedRevision: number | null;
  /** Removed state key → the state its cards move to. */
  migrationMap: Record<string, string> | null;
}

export interface PublishVersionResultDto {
  versionId: string;
  number: number;
  elsaDefinitionId?: string | null;
  elsaVersion: number;
}

export interface BoardColumnDto {
  key: string;
  title: string;
  color?: string | null;
  stateKeys: string[];
}

/** `columns: null` — no saved board, the default one column per state. */
export interface WorkflowBoardDto {
  columns?: BoardColumnDto[] | null;
  revision: number;
}

export interface SaveBoardRequestDto {
  columns: BoardColumnDto[];
  expectedRevision: number | null;
}

export interface SaveWorkflowBoardResultDto {
  revision: number;
}

export interface UnmappedStateDto {
  key?: string | null;
  name?: string | null;
  inPublishedVersion: boolean;
  inDraftVersion: boolean;
}

/** `TransitionDefinition.AnyState`. */
export const ANY_STATE = '*';

/**
 * Error codes the builder reacts to. Every domain error comes back as HTTP 400
 * with `{ errors: [{ errorMessage, errorCode, propertyName }] }`; there is no 409.
 */
export const WorkflowsApiErrorCode = {
  /** The draft was saved by someone else since it was read. */
  DraftStaleRevision: 'WorkflowDraft.StaleRevision',
  /** The board was saved by someone else since it was read. */
  BoardStaleRevision: 'WorkflowBoard.StaleRevision',
  /** Two saves of the same workflow raced; the later one lost at commit. */
  ConcurrentChange: 'Workflow.ConcurrentChange',
} as const;
export type WorkflowsApiErrorCode =
  (typeof WorkflowsApiErrorCode)[keyof typeof WorkflowsApiErrorCode];
